/**
 * Emote catalogue (spec §4.4: bounded soundboard, 24 launch emotes, 10 equippable slots, 3s
 * cooldown, per-player mute).
 *
 * Ids are the emoji codepoints used as frame names in `public/assets/atlases/emotes.json`, so the
 * scene can look a sticker up with `Texture.from(id)` and the wire never carries anything but a
 * short hex string. `glyph` is the DOM fallback the picker renders until the atlas is loaded.
 * `emotes.test.ts` asserts every id is a real frame in that atlas.
 */
export interface EmoteDef {
  id: string;
  glyph: string;
  label: string;
}

/** All 24 frames of the emotes atlas, in picker order. Copy stays table-side, never mean. */
export const EMOTE_CATALOG: readonly EmoteDef[] = [
  { id: '1f525', glyph: '🔥', label: 'On fire' },
  { id: '1f62d', glyph: '😭', label: 'Crying towel' },
  { id: '1f480', glyph: '💀', label: 'Dead' },
  { id: '1f44f', glyph: '👏', label: 'Clap' },
  { id: '1f440', glyph: '👀', label: 'Eyes on you' },
  { id: '1f624', glyph: '😤', label: "Set 'em" },
  { id: '1f9e2', glyph: '🧢', label: 'Cap' },
  { id: '1f451', glyph: '👑', label: 'Crown' },
  { id: '1f91d', glyph: '🤝', label: 'Good game' },
  { id: '1fae1', glyph: '🫡', label: 'Respect' },
  { id: '1f0cf', glyph: '🃏', label: 'Big Joker' },
  { id: '1f3af', glyph: '🎯', label: 'Called it' },
  { id: '1f4e3', glyph: '📣', label: 'Talk to your partner' },
  { id: '1fae3', glyph: '🫣', label: 'And a possible' },
  { id: '1f9f9', glyph: '🧹', label: 'Sweep' },
  { id: '1faa6', glyph: '🪦', label: 'RIP that bid' },
  { id: '1f60f', glyph: '😏', label: 'Smirk' },
  { id: '1f644', glyph: '🙄', label: 'Really' },
  { id: '1f485', glyph: '💅', label: 'Too easy' },
  { id: '1f976', glyph: '🥶', label: 'Cold' },
  { id: '1f921', glyph: '🤡', label: 'Clown move' },
  { id: '1fae0', glyph: '🫠', label: 'Melting' },
  { id: '1f634', glyph: '😴', label: 'Clock is running' },
  { id: '1f37f', glyph: '🍿', label: 'Popcorn' },
];

const BY_ID = new Map(EMOTE_CATALOG.map((e) => [e.id, e]));
export const emoteById = (id: string): EmoteDef | undefined => BY_ID.get(id);

export const EMOTE_COOLDOWN_MS = 3000;

/** Pure cooldown gate: `now` injectable so tests never wait. */
export class EmoteCooldown {
  private lastAt = -Infinity;

  constructor(
    readonly cooldownMs: number = EMOTE_COOLDOWN_MS,
    private readonly now: () => number = () => Date.now(),
  ) {}

  /** Milliseconds until the next emote is allowed (0 = ready). */
  remaining(): number {
    return Math.max(0, this.lastAt + this.cooldownMs - this.now());
  }

  ready(): boolean {
    return this.remaining() === 0;
  }

  /** Try to consume the cooldown; returns false (and does nothing) while cooling down. */
  fire(): boolean {
    if (!this.ready()) return false;
    this.lastAt = this.now();
    return true;
  }

  /** 0..1 progress of the current cooldown (1 = ready). */
  progress(): number {
    return 1 - this.remaining() / this.cooldownMs;
  }
}

/** Toggle an id in a mute list, returning a new array. */
export function toggleMuted(list: readonly string[], playerId: string): string[] {
  return list.includes(playerId) ? list.filter((id) => id !== playerId) : [...list, playerId];
}
